import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { isCompanyAdmin } from '../utils/planAccess';
import './NotificationSettingsSection.css';

const NotificationSettingsSection = ({ companyId }) => {
  const { t } = useTranslation(['company', 'common']);
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    notifyAssigned: true,
    notifyClaimed: true,
    notifyCompleted: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!companyId || !isCompanyAdmin(user)) {
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      try {
        const response = await axios.get(`/api/notifications/company/${companyId}/settings`);
        setSettings(response.data.data);
      } catch (err) {
        setError(formatError(err) || t('company:failedLoadNotificationSettings'));
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [companyId, user, t]);

  if (!isCompanyAdmin(user)) {
    return null;
  }

  const handleToggle = (field) => {
    setSettings((prev) => ({ ...prev, [field]: !prev[field] }));
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.patch(`/api/notifications/company/${companyId}/settings`, settings);
      setSettings(response.data.data);
      setSuccess(t('company:notificationSettingsSaved'));
    } catch (err) {
      setError(formatError(err) || t('company:failedSaveNotificationSettings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card notification-settings" id="notifications">
      <h2>{t('company:notificationsTitle')}</h2>
      <p className="section-description">
        {t('company:notificationsDescription')}
      </p>

      {loading ? (
        <p>{t('company:loadingNotificationSettings')}</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="toggle-row">
            <div className="toggle-label">
              <strong>{t('company:notifyAssignedTitle')}</strong>
              <span>{t('company:notifyAssignedHint')}</span>
            </div>
            <input
              type="checkbox"
              checked={settings.notifyAssigned}
              onChange={() => handleToggle('notifyAssigned')}
            />
          </div>

          <div className="toggle-row">
            <div className="toggle-label">
              <strong>{t('company:notifyClaimedTitle')}</strong>
              <span>{t('company:notifyClaimedHint')}</span>
            </div>
            <input
              type="checkbox"
              checked={settings.notifyClaimed}
              onChange={() => handleToggle('notifyClaimed')}
            />
          </div>

          <div className="toggle-row">
            <div className="toggle-label">
              <strong>{t('company:notifyCompletedTitle')}</strong>
              <span>{t('company:notifyCompletedHint')}</span>
            </div>
            <input
              type="checkbox"
              checked={settings.notifyCompleted}
              onChange={() => handleToggle('notifyCompleted')}
            />
          </div>

          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('common:saving') : t('company:saveNotificationSettings')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default NotificationSettingsSection;
